import React from 'react';
import type { Asset } from 'contentful';
import { ArrowIcon } from '../../AtomicComponents/TextLink';

import { WidgetProps, DEFAULT_BUTTON_TEXT, SharedProps } from '../shared';

interface StandardWidgetProps extends WidgetProps, SharedProps {
  onClick?: () => void;
}

export function StandardWidget({
  buttonText = DEFAULT_BUTTON_TEXT,
  buttonVariant = 'primary',
  buttonContent,
  icon,
  fullWidth,
  onClick
}: StandardWidgetProps) {
  const baseClasses = [
    'flex',
    'whitespace-nowrap',
    ...(fullWidth ? ['w-full'] : ['min-w-[200px]', 'max-w-[504px]']),
    'p-4',
    'justify-center',
    'items-center',
    'gap-1',
    'self-stretch',
    'rounded-button-shape',
    'h-12',
    'typography-button-md',
    'active:opacity-pressed',
    'focus:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-accent4-base',
    'focus-visible:ring-offset-1',
    'focus-visible:ring-offset-current'
  ];

  // Variant-specific classes
  const colorClasses =
    buttonVariant === 'sign-up'
      ? [
          'bg-callout',
          'text-on-callout',
          'h-10',
          'px-4',
          'py-3',
          'rounded-[8px]',
          'justify-center',
          'items-center'
        ]
      : buttonVariant === 'primary'
      ? ['bg-primary', 'text-on-primary-surface-dark']
      : ['bg-secondary', 'text-on-secondary-surface'];

  type ContentfulAssetFields = {
    file?: { url?: string };
    description?: string;
  };

  const iconAsset = icon as Asset | undefined;
  const iconFields = (iconAsset?.fields ??
    {}) as unknown as Partial<ContentfulAssetFields>;
  const iconFileUrl = iconFields.file?.url;
  const iconSrc = iconFileUrl ? `https:${iconFileUrl}` : undefined;
  const iconAlt = iconFields.description ?? '';

  const content = (
    <span
      className={[
        'inline-flex',
        'items-center',
        'justify-center',
        'transition-all'
      ].join(' ')}
    >
      <span
        data-testid="left-spacer"
        aria-hidden="true"
        className={[
          'inline-block',
          'align-middle',
          'transition-all',
          'w-4',
          'group-hover:w-0'
        ].join(' ')}
      />
      {iconSrc && (
        <span className="relative mr-1 inline-flex h-4 w-4 items-center justify-center align-middle">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={iconSrc}
            alt={iconAlt || ''}
            loading="lazy"
            className="h-4 w-4 align-middle"
          />
        </span>
      )}
      <span
        className={[
          'inline-flex',
          'items-center',
          buttonVariant === 'sign-up'
            ? ['text-center', 'font-medium', 'leading-4'].join(' ')
            : ''
        ].join(' ')}
      >
        {buttonContent || buttonText}
      </span>
      <ArrowIcon
        className={[
          'inline-block',
          '-translate-x-1',
          'opacity-0',
          'transition-all',
          'duration-200',
          'group-hover:translate-x-0.5',
          'group-hover:opacity-100',
          buttonVariant === 'primary' && 'invert'
        ].join(' ')}
        containerClassName={[
          'inline-flex',
          'h-4',
          'w-4',
          'items-center',
          'justify-center',
          'align-middle'
        ].join(' ')}
      />
    </span>
  );

  return (
    <div className={['flex', fullWidth ? 'w-full' : ''].join(' ').trim()}>
      <button
        type={onClick ? 'button' : 'submit'}
        role="link"
        aria-label={String(buttonText)}
        data-testid="standard-cta-btn"
        data-variant="desktop"
        onClick={onClick}
        className={[...baseClasses, ...colorClasses, 'relative', 'group'].join(
          ' '
        )}
      >
        {content}
      </button>
    </div>
  );
}
